/**
 * Types for server-persisted mission-planner drafts (#299 Feature G / #321).
 *
 * The backend stores the planner draft opaquely as a JSON `payload`; the UI is
 * the schema source of truth. We wrap the editable draft in a versioned envelope
 * so the shape can evolve without a server migration.
 */
import { MissionDraft } from './missionPlanner';

/** Current payload envelope version. Bump when the stored shape changes. */
export const SAVED_DRAFT_PAYLOAD_VERSION = 1 as const;

/**
 * The opaque payload persisted per draft: the full editable draft plus the
 * planner toggles the interchange formats can't carry (returnToHome).
 */
export interface SavedDraftPayload {
  version: typeof SAVED_DRAFT_PAYLOAD_VERSION;
  draft: MissionDraft;
  returnToHome: boolean;
}

/** Full saved draft as returned by GET /mission-drafts/{id} (includes payload). */
export interface SavedDraft {
  id: string;
  owner_id: string;
  name: string;
  /** Drone the draft is planned for, absent when none is assigned (#451). */
  drone_id?: string;
  revision: number;
  payload: SavedDraftPayload;
  created_at: string;
  updated_at: string;
}

/** List-view projection (no payload) returned by GET /mission-drafts. */
export interface SavedDraftSummary {
  id: string;
  name: string;
  revision: number;
  updated_at: string;
}

/** Paginated list envelope from GET /mission-drafts. */
export interface SavedDraftListResponse {
  drafts: SavedDraftSummary[];
  total: number;
  limit: number;
  offset: number;
}
